/**
 * Custom hook for managing trade booking form state and logic
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  TradeBookingForm,
  FormValidationErrors,
  TradeType,
  SettlementType,
  FixingReference,
  FixingOffset,
  getDefaultTradeBookingForm,
  getDefaultFixingReference,
} from '../types/tradeBooking';
import { CaptureConfidence } from '../types/tradeImageCapture';
import { calculateContraAmount } from '../utils/contraAmountCalculator';
import { calculateValueDate, calculateMaturityDate, calculateDaysDifference, addDays } from '../utils/dateCalculations';
import { parseMaturity, formatMaturity } from '../utils/maturityParser';
import { validateTradeBookingForm } from '../utils/tradeBookingValidation';
import { businessCalendar } from '../services/businessCalendar';

interface DateConventionForHook {
  tradeType: string;
  currencyPair: string;
  defaultPaymentDate: string;
  hasOverride: boolean;
  overridePaymentDate?: string;
}

interface UseTradeBookingFormProps {
  systemDate?: string; // YYYY-MM-DD from client's Firestore systemDate
  dateConventions?: DateConventionForHook[];
}

/** Convert YYYY-MM-DD string to local Date (avoids timezone shift from new Date("YYYY-MM-DD")) */
const parseSystemDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const useTradeBookingForm = (props?: UseTradeBookingFormProps) => {
  const [form, setForm] = useState<TradeBookingForm>(() => {
    const defaultForm = getDefaultTradeBookingForm();
    // Set default dates for Spot (business days)
    defaultForm.valueDate = businessCalendar.getNextBusinessDay(defaultForm.tradeDate); // T+0 (ensure business day)
    defaultForm.expiryDate = businessCalendar.addBusinessDays(defaultForm.tradeDate, 1); // T+1
    defaultForm.paymentDate = defaultForm.expiryDate; // Expiry Date + 0
    // Spot defaults to Entrega Física, so set valuta dates to expiry date
    defaultForm.currency1PaymentDate = defaultForm.expiryDate;
    defaultForm.currency2PaymentDate = defaultForm.expiryDate;
    return defaultForm;
  });

  // Keep a ref to dateConventions so handlers (useCallback with [] deps) always see the latest value
  const dateConventionsRef = useRef<DateConventionForHook[]>(props?.dateConventions ?? []);
  useEffect(() => {
    dateConventionsRef.current = props?.dateConventions ?? [];
  }, [props?.dateConventions]);

  // Resolve payment date from the configured convention for the current trade type + currency pair.
  // Falls back to expiryDate + 1 business day when no convention is found.
  const resolvePaymentDate = useCallback((
    expiryDate: Date,
    tradeType: TradeType | null,
    currency1: string,
    currency2: string,
  ): Date => {
    const conventions = dateConventionsRef.current;
    if (tradeType && currency1 && currency2 && conventions.length) {
      const currencyPair = `${currency1}/${currency2}`;
      const convention =
        conventions.find(c => c.tradeType === tradeType && c.currencyPair === currencyPair) ??
        conventions.find(c => c.tradeType === tradeType && c.currencyPair === 'All Others');
      if (convention) {
        const key = convention.hasOverride && convention.overridePaymentDate
          ? convention.overridePaymentDate
          : convention.defaultPaymentDate;
        switch (key) {
          case 'expiryDateOption': return expiryDate;
          case 'expiryDatePlus1': return businessCalendar.addBusinessDays(expiryDate, 1);
          case 'expiryDatePlus2': return businessCalendar.addBusinessDays(expiryDate, 2);
        }
      }
    }
    return businessCalendar.addBusinessDays(expiryDate, 1); // fallback
  }, []);

  // One-time sync: when systemDate first arrives, update tradeDate + dependent dates
  const systemDateApplied = useRef(false);
  useEffect(() => {
    if (!props?.systemDate || systemDateApplied.current) return;
    systemDateApplied.current = true;
    const sysDate = parseSystemDate(props.systemDate);
    setForm(prev => {
      const updated = { ...prev, tradeDate: sysDate };
      if (prev.tradeType === TradeType.SPOT) {
        updated.valueDate = businessCalendar.getNextBusinessDay(sysDate);
        updated.expiryDate = businessCalendar.addBusinessDays(sysDate, 1);
        updated.paymentDate = updated.expiryDate;
        updated.currency1PaymentDate = updated.expiryDate;
        updated.currency2PaymentDate = updated.expiryDate;
      } else if (prev.tradeType === TradeType.FORWARD) {
        updated.valueDate = businessCalendar.addBusinessDays(sysDate, 1);
      }
      return updated;
    });
  }, [props?.systemDate]);

  const [errors, setErrors] = useState<FormValidationErrors>({});
  const [touched, setTouched] = useState<Set<keyof TradeBookingForm>>(new Set());
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Trade Image Capture confidence state (KAN-82)
  const [captureConfidence, setCaptureConfidence] = useState<CaptureConfidence>({});
  const [captureRawText, setCaptureRawText] = useState<Record<string, string>>({});

  const setCaptureConfidenceValues = useCallback(
    (confidence: CaptureConfidence, rawText?: Record<string, string>) => {
      setCaptureConfidence(confidence);
      if (rawText) setCaptureRawText(rawText);
    },
    []
  );

  const clearFieldConfidence = useCallback((fieldName: string) => {
    setCaptureConfidence(prev => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
    setCaptureRawText(prev => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const clearAllConfidence = useCallback(() => {
    setCaptureConfidence({});
    setCaptureRawText({});
  }, []);

  // Re-validate form after submission when form changes
  useEffect(() => {
    if (hasSubmitted) {
      const validationErrors = validateTradeBookingForm(form);
      setErrors(validationErrors);
    }
    // Serialize dates to ensure React detects changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasSubmitted,
    form.tradeType,
    form.direction,
    form.amount,
    form.currency1,
    form.price,
    form.currency2,
    form.contraAmount,
    form.ourParty,
    form.counterparty,
    form.tradeDate?.toISOString(),
    form.valueDate?.toISOString(),
    form.expiryDate?.toISOString(),
    form.paymentDate?.toISOString(),
    form.maturity,
    form.fixingReference,
    form.fixingOffset,
    form.settlementType,
    form.settlementCurrency,
  ]);

  // Calculate contra amount whenever amount, price, or currency2 changes
  useEffect(() => {
    const contraAmount = calculateContraAmount(form.amount, form.price, form.currency2);
    if (contraAmount !== form.contraAmount) {
      setForm(prev => ({ ...prev, contraAmount }));
    }
  }, [form.amount, form.price, form.currency2]);

  // Update fixing reference when currency pair changes.
  // KAN-294 Fix D: skip for Entrega Física — fixing reference is always N/A and must not be overridden.
  useEffect(() => {
    if (
      form.currency1 && form.currency2 && form.tradeType === TradeType.FORWARD &&
      form.settlementType !== SettlementType.ENTREGA_FISICA
    ) {
      const newFixingRef = getDefaultFixingReference(form.currency1, form.currency2);
      if (newFixingRef !== form.fixingReference) {
        setForm(prev => ({ ...prev, fixingReference: newFixingRef }));
      }
    }
  }, [form.currency1, form.currency2, form.tradeType]);

  // CLF/CLP (Forward de Inflación) constraints:
  // CLF is not a deliverable currency so settlement must always be Compensación,
  // and settlement currency defaults to CLP.
  useEffect(() => {
    if (form.currency1 === 'CLF' && form.tradeType === TradeType.FORWARD) {
      setForm(prev => {
        const updates: Partial<TradeBookingForm> = {};
        if (prev.settlementType !== SettlementType.COMPENSACION) {
          updates.settlementType = SettlementType.COMPENSACION;
          updates.currency1PaymentDate = null;
          updates.currency2PaymentDate = null;
        }
        if (!prev.settlementCurrency) {
          updates.settlementCurrency = 'CLP';
        }
        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
      });
    }
  }, [form.currency1, form.tradeType]);

  // Handle trade type change
  const handleTradeTypeChange = useCallback((tradeType: TradeType) => {
    setForm(prev => {
      const updated = { ...prev, tradeType };

      if (tradeType === TradeType.SPOT) {
        // Spot defaults (business days)
        updated.valueDate = businessCalendar.getNextBusinessDay(prev.tradeDate); // T+0
        updated.expiryDate = businessCalendar.addBusinessDays(prev.tradeDate, 1); // T+1
        updated.paymentDate = updated.expiryDate; // Expiry Date + 0
        updated.settlementType = SettlementType.ENTREGA_FISICA;
        updated.maturity = '';
        updated.maturityDays = null;
        // Clear Forward Asiático and Forward Starting state
        updated.isAverageRateForward = false;
        updated.averageRateFixingSchedule = [];
        updated.isForwardStarting = false;
        updated.forwardStartingFixingSchedule = [];
        // Set valuta dates to expiry date for Entrega Física
        updated.currency1PaymentDate = updated.expiryDate;
        updated.currency2PaymentDate = updated.expiryDate;
        // CLF is not deliverable — clear it if selected when switching to Spot
        if (prev.currency1 === 'CLF') {
          updated.currency1 = '';
          updated.currency2 = '';
        }
      } else if (tradeType === TradeType.FORWARD) {
        // Forward defaults — leave maturity/expiryDate/paymentDate blank
        // for the user to fill in (varies per trade)
        updated.valueDate = businessCalendar.addBusinessDays(prev.tradeDate, 1); // T+1
        updated.maturity = '';
        updated.maturityDays = null;
        updated.expiryDate = null;
        updated.paymentDate = null;
        updated.fixingReference = getDefaultFixingReference(prev.currency1, prev.currency2);
        updated.fixingOffset = FixingOffset.ZERO;
        updated.settlementType = SettlementType.COMPENSACION;
        // Clear valuta dates for Compensación (they're not used)
        updated.currency1PaymentDate = null;
        updated.currency2PaymentDate = null;
      }

      return updated;
    });
  }, []);

  // Handle maturity change (Forward only)
  const handleMaturityChange = useCallback((maturity: string) => {
    const parseResult = parseMaturity(maturity);

    setForm(prev => {
      const updated = { ...prev, maturity };

      if (parseResult.success && prev.valueDate) {
        updated.maturityDays = parseResult.days;
        const rawExpiry = calculateMaturityDate(prev.valueDate, parseResult.days);
        updated.expiryDate = rawExpiry ? businessCalendar.getNextBusinessDay(rawExpiry) : null;
        if (updated.expiryDate) {
          updated.paymentDate = resolvePaymentDate(updated.expiryDate, prev.tradeType, prev.currency1, prev.currency2);
          // Update valuta dates to match expiry date if Entrega Física
          if (prev.settlementType === SettlementType.ENTREGA_FISICA) {
            updated.currency1PaymentDate = updated.expiryDate;
            updated.currency2PaymentDate = updated.expiryDate;
          }
        }
      } else {
        updated.maturityDays = null;
      }

      return updated;
    });
  }, []);

  // Handle expiry date change (Forward only) - recalculates maturity
  const handleExpiryDateChange = useCallback((expiryDate: Date | null) => {
    setForm(prev => {
      const updated = { ...prev, expiryDate };

      if (expiryDate && prev.valueDate) {
        const days = calculateDaysDifference(prev.valueDate, expiryDate);
        updated.maturityDays = days;
        updated.maturity = formatMaturity(days);
        updated.paymentDate = resolvePaymentDate(expiryDate, prev.tradeType, prev.currency1, prev.currency2);
        // Update valuta dates to match expiry date if Entrega Física
        if (prev.settlementType === SettlementType.ENTREGA_FISICA) {
          updated.currency1PaymentDate = expiryDate;
          updated.currency2PaymentDate = expiryDate;
        }
      }

      return updated;
    });
  }, []);

  // Handle value date change - recalculates expiry date
  const handleValueDateChange = useCallback((valueDate: Date | null) => {
    setForm(prev => {
      const updated = { ...prev, valueDate };

      if (prev.tradeType === TradeType.FORWARD && valueDate && prev.maturityDays) {
        const rawExpiry = calculateMaturityDate(valueDate, prev.maturityDays);
        updated.expiryDate = rawExpiry ? businessCalendar.getNextBusinessDay(rawExpiry) : null;
        if (updated.expiryDate) {
          updated.paymentDate = resolvePaymentDate(updated.expiryDate, prev.tradeType, prev.currency1, prev.currency2);
          // Update valuta dates to match expiry date if Entrega Física
          if (prev.settlementType === SettlementType.ENTREGA_FISICA) {
            updated.currency1PaymentDate = updated.expiryDate;
            updated.currency2PaymentDate = updated.expiryDate;
          }
        }
      } else if (prev.tradeType === TradeType.SPOT && valueDate) {
        // Spot: expiry is T+1 business day from trade date
        updated.expiryDate = businessCalendar.addBusinessDays(prev.tradeDate, 1);
        updated.paymentDate = updated.expiryDate;
        // Update valuta dates for Spot (always Entrega Física)
        updated.currency1PaymentDate = updated.expiryDate;
        updated.currency2PaymentDate = updated.expiryDate;
      }

      return updated;
    });
  }, []);

  // Handle Forward Asiático toggle
  const handleAverageRateForwardToggle = useCallback((enabled: boolean) => {
    setForm(prev => {
      const updated = { ...prev, isAverageRateForward: enabled };
      if (enabled) {
        updated.settlementType = SettlementType.COMPENSACION;
        updated.averageRateFixingSchedule = [];
        updated.currency1PaymentDate = null;
        updated.currency2PaymentDate = null;
        // Mutually exclusive with Forward Starting
        updated.isForwardStarting = false;
      } else {
        updated.averageRateFixingSchedule = [];
      }
      return updated;
    });
  }, []);

  // Handle Forward Starting toggle
  const handleForwardStartingToggle = useCallback((enabled: boolean) => {
    setForm(prev => {
      const updated = { ...prev, isForwardStarting: enabled };
      if (enabled) {
        // Price = 0 at booking (will display as "To Be Fixed")
        updated.price = 0;
        updated.spotPrice = null;
        // Default value date to T+2 business days (user can still modify)
        const defaultFixingDate = prev.tradeDate
          ? businessCalendar.addBusinessDays(prev.tradeDate, 2)
          : null;
        if (defaultFixingDate) {
          updated.valueDate = defaultFixingDate;
          // Initialize 1-element schedule with T+2 as default fixing date
          const pad2 = (n: number) => String(n).padStart(2, '0');
          const dateStr = `${defaultFixingDate.getFullYear()}-${pad2(defaultFixingDate.getMonth() + 1)}-${pad2(defaultFixingDate.getDate())}`;
          updated.forwardStartingFixingSchedule = [{ date: dateStr }];
        }
        // Default spot fixing reference to USD_OBS (always, separate from NDF fixingReference)
        updated.spotFixingReference = FixingReference.USD_OBS;
        // Mutually exclusive with Forward Asiático
        updated.isAverageRateForward = false;
        updated.averageRateFixingSchedule = [];
      } else {
        // Clear price so user can enter manually
        updated.price = null;
        updated.spotPrice = null;
        updated.forwardStartingFixingSchedule = [];
        updated.spotFixingReference = FixingReference.USD_OBS;
      }
      return updated;
    });
  }, []);

  // Add multiple fixing dates at once
  const handleFixingScheduleAddDates = useCallback((dates: string[]) => {
    setForm(prev => {
      const existing = new Set(prev.averageRateFixingSchedule.map(e => e.date));
      const newEntries = dates.filter(d => !existing.has(d)).map(d => ({ date: d }));
      return { ...prev, averageRateFixingSchedule: [...prev.averageRateFixingSchedule, ...newEntries] };
    });
  }, []);

  // Remove a single fixing date
  const handleFixingScheduleRemoveDate = useCallback((date: string) => {
    setForm(prev => ({
      ...prev,
      averageRateFixingSchedule: prev.averageRateFixingSchedule.filter(e => e.date !== date)
    }));
  }, []);

  // Clear all fixing dates
  const handleFixingScheduleClearAll = useCallback(() => {
    setForm(prev => ({ ...prev, averageRateFixingSchedule: [] }));
  }, []);

  // Forward Starting schedule handlers
  const handleForwardStartingScheduleAddDates = useCallback((dates: string[]) => {
    setForm(prev => {
      const existing = new Set(prev.forwardStartingFixingSchedule.map(e => e.date));
      const newEntries = dates.filter(d => !existing.has(d)).map(d => ({ date: d }));
      const updatedSchedule = [...prev.forwardStartingFixingSchedule, ...newEntries];
      const updated = { ...prev, forwardStartingFixingSchedule: updatedSchedule };
      // Auto-set ValueDate: 1 date → that date, 2+ dates → TradeDate
      if (updatedSchedule.length === 1 && updatedSchedule[0].date) {
        const [y, m, d] = updatedSchedule[0].date.split('-').map(Number);
        updated.valueDate = new Date(y, m - 1, d);
      } else if (updatedSchedule.length > 1 && prev.tradeDate) {
        updated.valueDate = prev.tradeDate;
      }
      return updated;
    });
  }, []);

  const handleForwardStartingScheduleRemoveDate = useCallback((date: string) => {
    setForm(prev => {
      const updatedSchedule = prev.forwardStartingFixingSchedule.filter(e => e.date !== date);
      const updated = { ...prev, forwardStartingFixingSchedule: updatedSchedule };
      // Auto-set ValueDate: 1 date → that date, 2+ dates → TradeDate, 0 dates → keep as is
      if (updatedSchedule.length === 1 && updatedSchedule[0].date) {
        const [y, m, d] = updatedSchedule[0].date.split('-').map(Number);
        updated.valueDate = new Date(y, m - 1, d);
      } else if (updatedSchedule.length > 1 && prev.tradeDate) {
        updated.valueDate = prev.tradeDate;
      }
      return updated;
    });
  }, []);

  const handleForwardStartingScheduleClearAll = useCallback(() => {
    setForm(prev => {
      const updated = { ...prev, forwardStartingFixingSchedule: [] };
      // Reset valueDate to T+2 when clearing all dates
      if (prev.tradeDate) {
        updated.valueDate = businessCalendar.addBusinessDays(prev.tradeDate, 2);
      }
      return updated;
    });
  }, []);

  // Handle settlement type change
  const handleSettlementTypeChange = useCallback((settlementType: SettlementType) => {
    setForm(prev => {
      if (prev.isAverageRateForward) return prev; // Locked to Compensación
      const updated = { ...prev, settlementType };

      if (settlementType === SettlementType.ENTREGA_FISICA) {
        // Set valuta dates to expiry date by default
        updated.currency1PaymentDate = prev.expiryDate;
        updated.currency2PaymentDate = prev.expiryDate;
        // Clear settlement currency (not used for Entrega Física)
        updated.settlementCurrency = '';
        // Fixing fields are N/A for Entrega Física
        updated.fixingReference = FixingReference.NA;
        updated.fixingOffset = FixingOffset.NA;
      } else {
        // Clear valuta dates for Compensación
        updated.currency1PaymentDate = null;
        updated.currency2PaymentDate = null;
        // Restore default fixing reference when switching back to Compensación
        updated.fixingReference = getDefaultFixingReference(prev.currency1, prev.currency2);
        updated.fixingOffset = FixingOffset.ZERO;
      }

      return updated;
    });
  }, []);

  // Bulk update — apply multiple fields in one setForm call (used by image capture)
  const bulkUpdateFields = useCallback((updates: Partial<TradeBookingForm>) => {
    setForm(prev => ({ ...prev, ...updates }));
    setTouched(prev => {
      const next = new Set(prev);
      for (const key of Object.keys(updates)) next.add(key as keyof TradeBookingForm);
      return next;
    });
  }, []);

  // Generic field update handler
  const updateField = useCallback(<K extends keyof TradeBookingForm>(
    field: K,
    value: TradeBookingForm[K]
  ) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setTouched(prev => new Set(prev).add(field));
    // Clear confidence highlight when user manually edits a captured field
    setCaptureConfidence(prev => {
      if (prev[field as string] !== undefined) {
        const next = { ...prev };
        delete next[field as string];
        return next;
      }
      return prev;
    });
  }, []);

  // Validate form
  const validate = useCallback((): { isValid: boolean; errors: FormValidationErrors } => {
    setHasSubmitted(true);
    const validationErrors = validateTradeBookingForm(form);
    setErrors(validationErrors);
    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors
    };
  }, [form]);

  // Mark field as touched
  const touchField = useCallback((field: keyof TradeBookingForm) => {
    setTouched(prev => new Set(prev).add(field));
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    const sysDate = props?.systemDate ? parseSystemDate(props.systemDate) : undefined;
    const defaultForm = getDefaultTradeBookingForm(sysDate);
    defaultForm.valueDate = businessCalendar.getNextBusinessDay(defaultForm.tradeDate);
    defaultForm.expiryDate = businessCalendar.addBusinessDays(defaultForm.tradeDate, 1);
    defaultForm.paymentDate = defaultForm.expiryDate;
    // Spot defaults to Entrega Física, so set valuta dates to expiry date
    defaultForm.currency1PaymentDate = defaultForm.expiryDate;
    defaultForm.currency2PaymentDate = defaultForm.expiryDate;
    setForm(defaultForm);
    setErrors({});
    setTouched(new Set());
    setHasSubmitted(false);
    setCaptureConfidence({});
    setCaptureRawText({});
  }, [props?.systemDate]);

  return {
    form,
    errors,
    touched,
    updateField,
    bulkUpdateFields,
    handleTradeTypeChange,
    handleMaturityChange,
    handleExpiryDateChange,
    handleValueDateChange,
    handleSettlementTypeChange,
    handleAverageRateForwardToggle,
    handleForwardStartingToggle,
    handleFixingScheduleAddDates,
    handleFixingScheduleRemoveDate,
    handleFixingScheduleClearAll,
    handleForwardStartingScheduleAddDates,
    handleForwardStartingScheduleRemoveDate,
    handleForwardStartingScheduleClearAll,
    touchField,
    validate,
    resetForm,
    // Trade Image Capture (KAN-82)
    captureConfidence,
    captureRawText,
    setCaptureConfidenceValues,
    clearFieldConfidence,
    clearAllConfidence,
  };
};
